import React, { useState } from 'react';
import { ministryInfo } from '../mock';
import { Mail, Phone, MapPin, Send } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    console.log('Contact form submission:', formData);

    try {
      const response = await fetch(`${API}/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      console.log('Response status:', response.status);
      
      const result = await response.json();
      console.log('Response data:', result);

      if (response.ok) {
        setSubmitMessage('Thank you for reaching out! We\'ll get back to you soon. Check your email for confirmation.');
        setFormData({
          name: '',
          email: '',
          subject: '',
          message: ''
        });
      } else {
        console.error('Form submission failed:', response.status, result);
        setSubmitMessage(result.detail || result.error || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      console.error('Contact form error:', error);
      setSubmitMessage('Unable to send message. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="section-padding" style={{ 
      background: 'linear-gradient(135deg, #F5F5DC 0%, #FFF8DC 100%)' 
    }}>
      <div className="container">
        <div style={{ textAlign: 'center', marginBottom: '80px' }}>
          <h2 className="hero-medium" style={{ marginBottom: '24px', color: '#8B4513' }}>
            Contact Us
          </h2>
          <p className="body-large" style={{ color: '#5D4037', maxWidth: '600px', margin: '0 auto' }}>
            Whether you need prayer, have questions about our services, or just want to connect, 
            don't hesitate to reach out.
          </p>
        </div>
        
        <div className="grid-two-column">
          <div>
            <h3 className="heading-2" style={{ marginBottom: '32px' }}>
              Contact Information
            </h3>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <Mail size={24} style={{ color: 'var(--text-primary)' }} />
                <div>
                  <p className="body-regular" style={{ marginBottom: '4px' }}>Email</p>
                  <a 
                    href={`mailto:${ministryInfo.contact.email}`}
                    className="body-regular"
                    style={{ color: 'var(--interactive-base)', textDecoration: 'none' }}
                  >
                    {ministryInfo.contact.email}
                  </a>
                </div>
              </div>
              
              <div style={{ 
                background: 'var(--bg-secondary)', 
                padding: '20px', 
                borderRadius: '0px',
                border: '1px solid var(--border-light)'
              }}>
                <h4 className="heading-3" style={{ marginBottom: '12px' }}>
                  Preferred Contact Method
                </h4>
                <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                  Email is the best way to reach us. We typically respond within 24 hours.
                </p>
              </div>
            </div>
            
            <div style={{ 
              marginTop: '48px',
              background: 'var(--bg-primary)',
              padding: '32px',
              borderRadius: '0px',
              border: '1px solid var(--border-light)'
            }}>
              <h4 className="heading-3" style={{ marginBottom: '16px' }}>
                Office Hours
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span className="body-regular">Monday - Friday</span>
                  <span className="body-regular" style={{ color: 'var(--text-secondary)' }}>9:00 AM - 5:00 PM</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span className="body-regular">Saturday</span>
                  <span className="body-regular" style={{ color: 'var(--text-secondary)' }}>By Appointment</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span className="body-regular">Sunday</span>
                  <span className="body-regular" style={{ color: 'var(--text-secondary)' }}>Closed</span>
                </div>
              </div>
            </div>
          </div>
          
          <div>
            <form onSubmit={handleSubmit} style={{ 
              background: 'var(--bg-primary)', 
              padding: '32px', 
              borderRadius: '0px',
              border: '1px solid var(--border-light)'
            }}>
              <h3 className="heading-2" style={{ marginBottom: '24px' }}>
                Send us a Message
              </h3>
              
              {submitMessage && (
                <div style={{
                  padding: '16px',
                  marginBottom: '24px',
                  background: submitMessage.includes('Thank you') ? 'var(--bg-subtle)' : 'var(--status-error)',
                  color: submitMessage.includes('Thank you') ? 'var(--status-success)' : 'var(--bg-primary)',
                  borderRadius: '0px',
                  fontSize: '14px',
                  lineHeight: '1.5'
                }}>
                  {submitMessage}
                </div>
              )}
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                <div>
                  <label className="body-regular" style={{ display: 'block', marginBottom: '8px' }}>
                    Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleInputChange}
                    disabled={isSubmitting}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '1px solid var(--border-medium)',
                      borderRadius: '0px',
                      background: 'var(--bg-primary)',
                      color: 'var(--text-primary)',
                      fontSize: '14px',
                      fontFamily: 'Crimson Text, serif',
                      opacity: isSubmitting ? 0.7 : 1
                    }}
                  />
                </div>
                
                <div>
                  <label className="body-regular" style={{ display: 'block', marginBottom: '8px' }}>
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    disabled={isSubmitting}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '1px solid var(--border-medium)',
                      borderRadius: '0px',
                      background: 'var(--bg-primary)',
                      color: 'var(--text-primary)',
                      fontSize: '14px',
                      fontFamily: 'Crimson Text, serif',
                      opacity: isSubmitting ? 0.7 : 1
                    }}
                  />
                </div>
                
                <div>
                  <label className="body-regular" style={{ display: 'block', marginBottom: '8px' }}>
                    Subject
                  </label>
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    disabled={isSubmitting}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '1px solid var(--border-medium)',
                      borderRadius: '0px',
                      background: 'var(--bg-primary)',
                      color: 'var(--text-primary)',
                      fontSize: '14px',
                      fontFamily: 'Crimson Text, serif',
                      opacity: isSubmitting ? 0.7 : 1
                    }}
                  />
                </div>
                
                <div>
                  <label className="body-regular" style={{ display: 'block', marginBottom: '8px' }}>
                    Message *
                  </label>
                  <textarea
                    name="message"
                    required
                    rows="5"
                    value={formData.message}
                    onChange={handleInputChange}
                    disabled={isSubmitting}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '1px solid var(--border-medium)',
                      borderRadius: '0px',
                      background: 'var(--bg-primary)',
                      color: 'var(--text-primary)',
                      fontSize: '14px',
                      fontFamily: 'Crimson Text, serif',
                      resize: 'vertical',
                      opacity: isSubmitting ? 0.7 : 1
                    }}
                  />
                </div>
                
                <button 
                  type="submit"
                  className="btn-primary"
                  disabled={isSubmitting}
                  style={{ 
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    opacity: isSubmitting ? 0.7 : 1,
                    cursor: isSubmitting ? 'not-allowed' : 'pointer'
                  }}
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                  <Send size={16} />
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};